import { useState } from 'react';
import slackIcon from '@/assets/protocols/menu-slack.svg';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Check, ChevronDown, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { INSTRUCTION_ACTIONS } from '../constants';
import { ASSIGN_TARGET_LABELS, BRAND_DEFAULT_LABEL, isEmail } from '@/modules/escalation-policy/escalation-policy';
import type {
  Agent,
  AssignActionData,
  InstructionActionData,
  InstructionAssignTarget,
  InstructionUnassignTarget,
  UnassignActionData
} from '../types';
import { getAvatarInitials } from '../utils/avatar';

type ActionType = (typeof INSTRUCTION_ACTIONS)[keyof typeof INSTRUCTION_ACTIONS];

/** A Kim user with the Sentinel role, as Assign to Sentinel picks one. */
export interface SentinelOption {
  id: string;
  name: string;
  email?: string;
}

interface InstructionActionsProps {
  /** Opens the composer for Send Email, Send Private Note or Send Slack Message. */
  onActionClick: (actionType: ActionType) => void;
  onAssign: (assign: AssignActionData) => void;
  onUnassign: (target: InstructionUnassignTarget) => void;
  agents?: Agent[];
  sentinels?: SentinelOption[];
  className?: string;
  disabled?: boolean;
  actionData?: InstructionActionData[] | null;
  onActionEdit?: (actionType: ActionType) => void;
  /** Takes the Assign or the Unassign off the instruction. */
  onActionRemove?: (actionType: ActionType) => void;
}

const TARGETS: InstructionAssignTarget[] = ['agent', 'sentinel'];
/** Unassign may also leave its target to the brand's default. */
const UNASSIGN_TARGETS: InstructionUnassignTarget[] = ['agent', 'sentinel', ''];

const targetLabel = (target: InstructionUnassignTarget): string => (target ? ASSIGN_TARGET_LABELS[target] : BRAND_DEFAULT_LABEL);

/** The person an Assign names, for its chip; null when the brand policy decides. */
const assigneeName = (assign: AssignActionData): string | null =>
  (assign.target === 'sentinel' ? assign.sentinelUserName || assign.sentinelUserEmail : assign.agentName || assign.agentEmail) || null;

export const InstructionActions = ({
  onActionClick,
  onAssign,
  onUnassign,
  agents = [],
  sentinels = [],
  className,
  disabled = false,
  actionData = null,
  onActionEdit,
  onActionRemove
}: InstructionActionsProps) => {
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [unassignOpen, setUnassignOpen] = useState(false);
  // An agent the helpdesk does not list is typed in by email.
  const [agentSearch, setAgentSearch] = useState('');
  const typedEmail = isEmail(agentSearch) ? agentSearch.trim() : null;
  const [actionsOpen, setActionsOpen] = useState(false);
  // Assign and Unassign ask who they act on before they are added.
  const [targetFor, setTargetFor] = useState<'assign' | 'unassign' | null>(null);

  // The escalations, as in the protocol "/" menu; each falls back to the brand's escalation policy.
  const availableActions: { type: ActionType; label: string; icon: string }[] = [
    { type: INSTRUCTION_ACTIONS.SEND_EMAIL, label: 'Send Email', icon: '/assets/escalate/kim-action.svg' },
    { type: INSTRUCTION_ACTIONS.ASSIGN, label: 'Assign', icon: '/assets/escalate/escalate.svg' },
    { type: INSTRUCTION_ACTIONS.UNASSIGN, label: 'Unassign', icon: '/assets/escalate/escalate.svg' },
    { type: INSTRUCTION_ACTIONS.PRIVATE_NOTE, label: 'Send Private Note', icon: '/assets/escalate/kim-action.svg' },
    { type: INSTRUCTION_ACTIONS.SEND_SLACK_MESSAGE, label: 'Send Slack Message', icon: slackIcon }
  ];

  const assign = actionData?.find((action): action is AssignActionData => action.type === INSTRUCTION_ACTIONS.ASSIGN);
  const unassign = actionData?.find((action): action is UnassignActionData => action.type === INSTRUCTION_ACTIONS.UNASSIGN);
  const hasSendEmail = actionData?.some((action) => action.type === INSTRUCTION_ACTIONS.SEND_EMAIL);
  const hasPrivateNote = actionData?.some((action) => action.type === INSTRUCTION_ACTIONS.PRIVATE_NOTE);
  const hasSlack = actionData?.some((action) => action.type === INSTRUCTION_ACTIONS.SEND_SLACK_MESSAGE);

  /** An Assign naming no one: the brand policy's default for that target decides. */
  const assignTo = (target: InstructionAssignTarget) => onAssign({ type: 'KIM_ASSIGN', target });
  const pickAgent = (agent: Agent) =>
    onAssign({
      type: 'KIM_ASSIGN',
      target: 'agent',
      agentId: agent.id,
      agentName: agent.name,
      agentEmail: agent.email ?? '',
      ...(agent.provider && { agentProvider: agent.provider })
    });
  const pickSentinel = (sentinel: SentinelOption) =>
    onAssign({
      type: 'KIM_ASSIGN',
      target: 'sentinel',
      sentinelUserId: sentinel.id,
      sentinelUserName: sentinel.name,
      sentinelUserEmail: sentinel.email ?? ''
    });

  const handleActionSelect = (actionType: ActionType) => {
    if (actionType === INSTRUCTION_ACTIONS.ASSIGN || actionType === INSTRUCTION_ACTIONS.UNASSIGN) {
      setTargetFor(actionType === INSTRUCTION_ACTIONS.ASSIGN ? 'assign' : 'unassign');
      return;
    }
    onActionClick(actionType);
    setActionsOpen(false);
  };

  const handleTargetSelect = (target: InstructionUnassignTarget) => {
    if (targetFor === 'unassign') onUnassign(target);
    else if (target) {
      // Re-picking the target the Assign already has keeps its person.
      if (assign?.target !== target) assignTo(target);
      // Straight on to the person; leaving it on Brand policy is a choice too.
      setEscalateOpen(true);
    }
    setTargetFor(null);
    setActionsOpen(false);
  };

  const pillClass = 'h-6 rounded-full bg-red-100 hover:bg-red-200 px-3 gap-2 text-xs font-medium text-red-600';

  return (
    <div className={cn('space-y-3', className)}>
      {/* First Row - Action Buttons */}
      <div className='flex items-center gap-2 flex-wrap'>
        {assign && (
          <Popover open={escalateOpen} onOpenChange={setEscalateOpen}>
            <PopoverTrigger asChild>
              <Button variant='ghost' role='combobox' aria-expanded={escalateOpen} disabled={disabled} className={pillClass}>
                <img src='/assets/escalate/escalate.svg' alt='Assign' className='w-3.5 h-3.5' />
                <span className='text-gray-800'>
                  Assign to {ASSIGN_TARGET_LABELS[assign.target]}: {assigneeName(assign) ?? <span className='italic'>Brand policy</span>}
                </span>
                <ChevronDown className='w-3 h-3 opacity-50' />
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-[250px] p-0' align='start'>
              <Command>
                <CommandInput
                  placeholder={assign.target === 'sentinel' ? 'Search Sentinels...' : 'Search agents or type an email...'}
                  className='h-9'
                  value={agentSearch}
                  onValueChange={setAgentSearch}
                />
                <CommandList className='max-h-[300px] overflow-y-auto'>
                  <CommandEmpty>
                    {assign.target === 'sentinel' ? 'No Sentinel found.' : 'No helpdesk agent found. Type their email to assign by email.'}
                  </CommandEmpty>
                  <CommandGroup heading='Assign to'>
                    {TARGETS.map((target) => (
                      <CommandItem
                        key={target}
                        value={`target ${ASSIGN_TARGET_LABELS[target]}`}
                        onSelect={() => {
                          if (target !== assign.target) assignTo(target);
                          setAgentSearch('');
                        }}
                      >
                        <span className='text-sm'>{ASSIGN_TARGET_LABELS[target]}</span>
                        <Check className={cn('ml-auto h-4 w-4', assign.target === target ? 'opacity-100' : 'opacity-0')} />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  {assign.target === 'agent' && typedEmail ? (
                    <CommandGroup heading='By email'>
                      <CommandItem
                        value={typedEmail}
                        onSelect={() => {
                          pickAgent({ id: '', name: typedEmail, email: typedEmail });
                          setAgentSearch('');
                          setEscalateOpen(false);
                        }}
                      >
                        <span className='text-sm'>Assign to {typedEmail}</span>
                      </CommandItem>
                    </CommandGroup>
                  ) : null}
                  <CommandGroup heading='Brand policy'>
                    <CommandItem
                      value='Use brand policy'
                      onSelect={() => {
                        assignTo(assign.target);
                        setEscalateOpen(false);
                      }}
                    >
                      <span className='text-sm'>Use brand policy</span>
                      <Check className={cn('ml-auto h-4 w-4', assigneeName(assign) ? 'opacity-0' : 'opacity-100')} />
                    </CommandItem>
                    {onActionRemove ? (
                      <CommandItem
                        value='Remove Assign'
                        onSelect={() => {
                          onActionRemove(INSTRUCTION_ACTIONS.ASSIGN);
                          setAgentSearch('');
                          setEscalateOpen(false);
                        }}
                      >
                        <span className='text-sm text-red-600'>Remove Assign</span>
                      </CommandItem>
                    ) : null}
                  </CommandGroup>
                  {assign.target === 'sentinel' ? (
                    <CommandGroup heading='Sentinels'>
                      {sentinels.map((sentinel) => (
                        <CommandItem
                          key={sentinel.id}
                          value={`${sentinel.name} ${sentinel.email ?? ''} ${sentinel.id}`}
                          onSelect={() => {
                            pickSentinel(sentinel);
                            setEscalateOpen(false);
                          }}
                        >
                          <div className='flex items-center gap-2 flex-1'>
                            <Avatar className='h-6 w-6'>
                              <AvatarFallback className='text-xs'>{getAvatarInitials(sentinel.name)}</AvatarFallback>
                            </Avatar>
                            <div className='flex flex-col'>
                              <span className='text-sm font-medium'>{sentinel.name}</span>
                              {sentinel.email ? <span className='text-xs text-muted-foreground'>{sentinel.email}</span> : null}
                            </div>
                          </div>
                          <Check className={cn('ml-auto h-4 w-4', assign.sentinelUserId === sentinel.id ? 'opacity-100' : 'opacity-0')} />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ) : (
                    <CommandGroup heading='Helpdesk agents'>
                      {agents.map((agent) => (
                        <CommandItem
                          key={`${agent.provider ?? ''}:${agent.id}`}
                          value={`${agent.name} ${agent.email ?? ''} ${agent.provider ?? ''}:${agent.id}`}
                          onSelect={() => {
                            pickAgent(agent);
                            setEscalateOpen(false);
                          }}
                        >
                          <div className='flex items-center gap-2 flex-1'>
                            <Avatar className='h-6 w-6'>
                              <AvatarImage src={agent.avatar} alt={agent.name} />
                              <AvatarFallback className='text-xs'>{getAvatarInitials(agent.name)}</AvatarFallback>
                            </Avatar>
                            <div className='flex flex-col'>
                              <span className='text-sm font-medium'>{agent.name}</span>
                              <span className='text-xs text-muted-foreground'>
                                {[agent.email, agent.provider && agent.provider.charAt(0).toUpperCase() + agent.provider.slice(1)].filter(Boolean).join(' · ')}
                              </span>
                            </div>
                          </div>
                          <Check
                            className={cn(
                              'ml-auto h-4 w-4',
                              assign.agentId === agent.id && (assign.agentProvider ?? '') === (agent.provider ?? '') ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}

        {unassign && (
          <Popover open={unassignOpen} onOpenChange={setUnassignOpen}>
            <PopoverTrigger asChild>
              <Button variant='ghost' role='combobox' aria-expanded={unassignOpen} disabled={disabled} className={pillClass}>
                <img src='/assets/escalate/escalate.svg' alt='Unassign' className='w-3.5 h-3.5' />
                <span className='text-gray-800'>
                  {unassign.target ? `Unassign ${ASSIGN_TARGET_LABELS[unassign.target]}` : `Unassign : ${BRAND_DEFAULT_LABEL}`}
                </span>
                <ChevronDown className='w-3 h-3 opacity-50' />
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-[200px] p-0' align='start'>
              <Command>
                <CommandInput placeholder='Search...' className='h-9' />
                <CommandList>
                  <CommandEmpty>No target found.</CommandEmpty>
                  <CommandGroup heading='Unassign'>
                    {UNASSIGN_TARGETS.map((target) => (
                      <CommandItem
                        key={target || 'default'}
                        value={targetLabel(target)}
                        onSelect={() => {
                          onUnassign(target);
                          setUnassignOpen(false);
                        }}
                      >
                        <span className='text-sm'>{targetLabel(target)}</span>
                        <Check className={cn('ml-auto h-4 w-4', (unassign.target ?? '') === target ? 'opacity-100' : 'opacity-0')} />
                      </CommandItem>
                    ))}
                    {onActionRemove ? (
                      <CommandItem
                        value='Remove Unassign'
                        onSelect={() => {
                          onActionRemove(INSTRUCTION_ACTIONS.UNASSIGN);
                          setUnassignOpen(false);
                        }}
                      >
                        <span className='text-sm text-red-600'>Remove Unassign</span>
                      </CommandItem>
                    ) : null}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}

        {/* Actions Dropdown */}
        <Popover
          open={actionsOpen}
          onOpenChange={(open) => {
            setActionsOpen(open);
            if (!open) setTargetFor(null);
          }}
        >
          <PopoverTrigger asChild>
            <Button
              variant='outline'
              role='combobox'
              aria-expanded={actionsOpen}
              disabled={disabled}
              type='button'
              className='h-6 rounded-full bg-muted hover:bg-muted/80 px-3 gap-2 text-xs font-medium'
            >
              <Plus className='w-3 h-3' />
              <span>Actions</span>
              <ChevronDown className='w-3 h-3 opacity-50' />
            </Button>
          </PopoverTrigger>
          <PopoverContent className='w-[250px] p-0' align='start'>
            {targetFor ? (
              <Command key='target'>
                <CommandInput placeholder='Search...' className='h-9' />
                <CommandList className='max-h-[300px] overflow-y-auto'>
                  <CommandEmpty>No target found.</CommandEmpty>
                  <CommandGroup heading={targetFor === 'assign' ? 'Assign to' : 'Unassign'}>
                    {(targetFor === 'unassign' ? UNASSIGN_TARGETS : TARGETS).map((target) => (
                      <CommandItem key={target || 'default'} value={targetLabel(target)} onSelect={() => handleTargetSelect(target)}>
                        <span className='text-sm font-medium'>{targetLabel(target)}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            ) : (
              <Command key='actions'>
                <CommandInput placeholder='Search action...' className='h-9' />
                <CommandList className='max-h-[300px] overflow-y-auto'>
                  <CommandEmpty>No action found.</CommandEmpty>
                  <CommandGroup>
                    {availableActions.map((action) => (
                      <CommandItem key={action.type} value={action.label} onSelect={() => handleActionSelect(action.type)}>
                        <div className='flex items-center gap-2 flex-1'>
                          <img src={action.icon} alt={action.label} className='w-4 h-4' />
                          <span className='text-sm font-medium italic'>{action.label}</span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            )}
          </PopoverContent>
        </Popover>
      </div>

      {/* Second Row - Saved Actions */}
      {(hasSendEmail || hasPrivateNote || hasSlack) && (
        <div className='flex flex-wrap gap-2'>
          {/* Send Email - Only show if saved */}
          {hasSendEmail && (
            <Button
              variant='outline'
              disabled={disabled}
              onClick={() => onActionEdit?.(INSTRUCTION_ACTIONS.SEND_EMAIL)}
              className='h-6 rounded-full bg-white hover:bg-gray-50 px-3 gap-2 text-xs font-medium'
            >
              <img src='/assets/escalate/kim-action.svg' alt='Kim Action' className='w-4 h-4' />
              <span className='text-gray-900 italic'>Send Email</span>
            </Button>
          )}

          {/* Private Note - Only show if saved */}
          {hasPrivateNote && (
            <Button
              variant='outline'
              disabled={disabled}
              onClick={() => onActionEdit?.(INSTRUCTION_ACTIONS.PRIVATE_NOTE)}
              className='h-6 rounded-full bg-white hover:bg-gray-50 px-3 gap-2 text-xs font-medium'
            >
              <img src='/assets/escalate/kim-action.svg' alt='Kim Action' className='w-4 h-4' />
              <span className='text-gray-900 italic'>Send Private Note</span>
            </Button>
          )}

          {hasSlack && (
            <Button
              variant='outline'
              disabled={disabled}
              onClick={() => onActionEdit?.(INSTRUCTION_ACTIONS.SEND_SLACK_MESSAGE)}
              className='h-6 rounded-full bg-white hover:bg-gray-50 px-3 gap-2 text-xs font-medium'
            >
              <img src={slackIcon} alt='Slack' className='w-4 h-4' />
              <span className='text-gray-900 italic'>Send Slack Message</span>
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
